import dayjs, { Dayjs } from 'dayjs';
import { RecurrentAgendaEvent, RecurrentInstanceAgendaEvent } from '../models';

export function recurentAgendaEventTransform(
  recurentEvent: RecurrentAgendaEvent,
  start: Dayjs,
  end: Dayjs
): RecurrentInstanceAgendaEvent[] {
  const eventEnd = recurentEvent.recurrent.endDate
    ? dayjs(recurentEvent.recurrent.endDate).tz(recurentEvent.timezone)
    : end;

  // Check if the period overlap the recurent period
  if (
    start.isAfter(eventEnd) ||
    end.isBefore(dayjs(recurentEvent.recurrent.startDate))
  ) {
    return [];
  }

  const collection: RecurrentInstanceAgendaEvent[] = [];
  const startGeneration = dayjs(recurentEvent.recurrent.startDate).tz(
    recurentEvent.timezone
  );
  const endGeneration = end.isBefore(eventEnd) ? end : eventEnd;

  if (recurentEvent.recurrent.freq === 'day') {
    generateDailyInstances(
      recurentEvent,
      start,
      startGeneration,
      endGeneration,
      collection
    );
  } else if (recurentEvent.recurrent.freq === 'week') {
    generateWeeklyInstances(
      recurentEvent,
      start,
      startGeneration,
      endGeneration,
      collection
    );
  }

  return collection;
}

function generateDailyInstances(
  recurentEvent: RecurrentAgendaEvent,
  start: Dayjs,
  startGeneration: Dayjs,
  endGeneration: Dayjs,
  collection: RecurrentInstanceAgendaEvent[]
) {
  let iter = startGeneration;

  while (iter.isBefore(endGeneration)) {
    const currentDay = iter.tz(recurentEvent.timezone);

    if (!currentDay.isBefore(start)) {
      const instance = generateInstanceFromRecurrent(recurentEvent, currentDay);
      if (!isDeleted(instance, recurentEvent)) {
        collection.push(instance);
      }
    }

    iter = iter.add(recurentEvent.recurrent.every, 'days');
  }
}

function generateWeeklyInstances(
  recurentEvent: RecurrentAgendaEvent,
  start: Dayjs,
  startGeneration: Dayjs,
  endGeneration: Dayjs,
  collection: RecurrentInstanceAgendaEvent[]
) {
  let iter = startGeneration;

  while (iter.isSameOrBefore(endGeneration, 'week')) {
    for (let i = 0; i < 7; i++) {
      const dayOfWeek = iter.add(i, 'days').tz(recurentEvent.timezone);

      if (
        !dayOfWeek.isBefore(start) &&
        !dayOfWeek.isAfter(endGeneration) &&
        recurentEvent.recurrent.daysOfWeek.includes(dayOfWeek.day())
      ) {
        const instance = generateInstanceFromRecurrent(
          recurentEvent,
          dayOfWeek
        );
        if (!isDeleted(instance, recurentEvent)) {
          collection.push(instance);
        }
      }
    }

    iter = iter.add(recurentEvent.recurrent.every, 'weeks');
  }
}

function generateInstanceFromRecurrent(
  recurentEvent: RecurrentAgendaEvent,
  day: Dayjs
): RecurrentInstanceAgendaEvent {
  const instance: RecurrentInstanceAgendaEvent = JSON.parse(
    JSON.stringify(recurentEvent)
  );

  // Remove/change some attributes
  delete (instance as any).recurrent;
  instance.id = globalThis.crypto.randomUUID();
  instance.generatedFrom = recurentEvent.id;

  // Retrieve the local start and end time of the recurrent event
  const recurrentStartMoment = dayjs(recurentEvent.startTime).tz(
    recurentEvent.timezone
  );
  const recurrentEndMoment = dayjs(recurentEvent.endTime).tz(
    recurentEvent.timezone
  );

  // Apply the occurrence date to the local start and end time
  // This ensures correct handling of daylight saving time transitions
  const instanceStartMoment = day
    .hour(recurrentStartMoment.hour())
    .minute(recurrentStartMoment.minute())
    .second(recurrentStartMoment.second())
    .millisecond(recurrentStartMoment.millisecond())
    .tz(recurentEvent.timezone);

  const durationMinutes = recurrentEndMoment.diff(
    recurrentStartMoment,
    'minutes'
  );
  const instanceEndMoment = instanceStartMoment
    .clone()
    .add(durationMinutes, 'minutes');

  instance.startTime = instanceStartMoment.format();
  instance.endTime = instanceEndMoment.format();

  return instance;
}

function isDeleted(
  instance: RecurrentInstanceAgendaEvent,
  recurentEvent: RecurrentAgendaEvent
): boolean {
  const deletes = recurentEvent.recurrent.daysDeletes;
  if (!deletes || deletes.length === 0) return false;

  return deletes.some(
    (del) =>
      dayjs(instance.startTime).isSame(del.startDate, 'minute') ||
      dayjs(instance.endTime).isSame(del.endDate, 'minute')
  );
}
